import { useState, useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom';

import { MotionConfig } from 'framer-motion';

import './App.css';
import './index.css';
import './classic-theme.css';
import 'katex/dist/katex.min.css';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { CreditsProvider } from './contexts/CreditsContext';
import { Toaster } from 'sonner';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

import LandingPage from './components/LandingPage';
import LoadingScreen from './components/LoadingScreen';
import AuthPage from './components/AuthPage';
import AuthCallback from './components/AuthCallback';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import EmailVerificationPage from './components/EmailVerificationPage';

import Layout from './components/Layout';
import Dashboard from './components/Dashboard';
import ChatPage from './components/ChatPage';
import SyllabusPage from './components/SyllabusPage';
import ProgressPage from './components/ProgressPage';
import QuizArena from './components/QuizArena';
import ProfilePage from './components/ProfilePage';
import SettingsPage from './components/SettingsPage';
import LeaderboardPage from './components/LeaderboardPage';
import MockExamPage from './components/MockExamPage';
import StudyPlanPage from './components/StudyPlanPage';
import PricingPage from './components/PricingPage';

import OnboardingModal from './components/OnboardingModal';
import SchoolSelectModal from './components/SchoolSelectModal';
import UpgradePromptModal from './components/UpgradePromptModal';
import GradeAccessGuard from './components/GradeAccessGuard';
import NewUserTutorial, { shouldShowTutorial } from './components/NewUserTutorial';
import './learning-guide.css';

import AdminDashboard from './components/AdminDashboard';
import TrafficSignalPage from './components/TrafficSignalPage';




/*
 * Handles authenticated users.
 *
 * IMPORTANT ORDER:
 *
 * 1. No school yet
 *    -> Existing SchoolSelectModal appears before app pages mount
 *
 * 2. School exists but onboarding is incomplete
 *    -> OnboardingModal
 *
 * 3. School + onboarding complete
 *    -> Normal app + tutorial if needed
 */
function ProtectedRoute({ children }) {
  const { user, loading, refresh } = useAuth();
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    /*
     * Tutorial can ONLY appear after:
     *
     * - the user exists
     * - a school has been selected
     * - onboarding is complete
     * - tutorial hasn't been seen
     */
    if (
      user &&
      user.school &&
      user.is_onboarded &&
      user.is_tutorial_seen === false && shouldShowTutorial(user.user_id)
    ) {
      setShowTutorial(true);
    } else {
      setShowTutorial(false);
    }
  }, [user]);

  useEffect(() => {
    const replay = () => { if (user?.school && user?.is_onboarded) setShowTutorial(true); };
    window.addEventListener('aceit:start-tour', replay);
    return () => window.removeEventListener('aceit:start-tour', replay);
  }, [user?.school, user?.is_onboarded]);

  if (loading) {
    return <LoadingScreen />;
  }

  /*
   * User signed out.
   *
   * Normally RootGate handles this, but keeping this here
   * makes the protected route itself safe too.
   */
  if (!user) {
    return <Navigate to="/" replace />;
  }

  /*
   * New Google users start with no school.
   *
   * DO NOT show onboarding yet.
   *
   * Reuse the existing modal before mounting Layout or any curriculum pages.
   */
  if (!user.school) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <SchoolSelectModal />
      </div>
    );
  }

  /*
   * School selected, but onboarding isn't complete yet.
   */
  if (!user.is_onboarded) {
    return (
      <div className="min-h-screen bg-zinc-950">
        <OnboardingModal
          onComplete={() => {
            refresh?.();
          }}
        />
      </div>
    );
  }

  /*
   * Fully onboarded user.
   */
  return (
    <>
      {children}

      <UpgradePromptModal />

      <GradeAccessGuard />

      {showTutorial && (
        <NewUserTutorial
          onClose={() => setShowTutorial(false)}
        />
      )}
    </>
  );
}


/*
 * ROOT BEHAVIOR
 *
 * This is the important part of the remembering system.
 *
 * If the browser still has a valid authenticated session:
 *     -> show the main app
 *
 * If there is no authenticated session:
 *     -> show the Landing Page
 *
 * There is NO sessionStorage/localStorage decision here.
 * The backend auth cookie is the source of truth.
 */
function RootGate() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  /*
   * SIGNED OUT / FIRST VISIT
   * -> Landing Page
   */
  if (!user) {
    return <LandingPage />;
  }

  /*
   * SIGNED IN
   * -> Main application
   *
   * ProtectedRoute handles:
   * School Select
   * Onboarding
   * Tutorial
   */
  return (
    <ProtectedRoute>
      <Layout />
    </ProtectedRoute>
  );
}


/*
 * Authentication pages should not be shown to a user
 * who is already logged in.
 */
function PublicAuthRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}


/*
 * Handles OAuth callback.
 */
function AppRouter() {
  const location = useLocation();

  /*
   * Support both:
   *
   * #session_id=...
   * ?session_id=...
   */
  const hasOAuth =
    location.hash?.includes('session_id=') ||
    location.search?.includes('session_id=');

  if (hasOAuth) {
    return <AuthCallback />;
  }

  return (
    <Routes>

      {/* Login / Signup */}
      <Route
        path="/login"
        element={
          <PublicAuthRoute>
            <AuthPage />
          </PublicAuthRoute>
        }
      />

      {/* Public legal pages */}
      <Route
        path="/terms"
        element={<TermsPage />}
      />

      <Route
        path="/privacy"
        element={<PrivacyPage />}
      />

      <Route
        path="/verify-email"
        element={<EmailVerificationPage />}
      />

      <Route
        path="/resend-verification"
        element={<EmailVerificationPage />}
      />

      {/* Secret traffic simulation — accessible via direct URL only */}
      <Route
        path="/traffic-signal"
        element={<TrafficSignalPage />}
      />


      /*
       * ROOT APPLICATION
       *
       * RootGate decides:
       *
       * signed out -> LandingPage
       * signed in  -> ProtectedRoute -> Layout
       */
      <Route
        path="/"
        element={<RootGate />}
      >
        {/* Main Dashboard */}
        <Route
          index
          element={<Dashboard />}
        />

        <Route
          path="dashboard"
          element={<Dashboard />}
        />

        {/* AI Tutor */}
        <Route
          path="chat"
          element={<ChatPage />}
        />

        <Route
          path="chat/:sessionId"
          element={<ChatPage />}
        />

        {/* Syllabus */}
        <Route
          path="syllabus"
          element={<SyllabusPage />}
        />

        {/* Progress */}
        <Route
          path="progress"
          element={<ProgressPage />}
        />

        {/* Quiz Arena */}
        <Route
          path="quiz"
          element={<QuizArena />}
        />

        {/* Leaderboard */}
        <Route
          path="leaderboard"
          element={<LeaderboardPage />}
        />

        {/* Mock Exams */}
        <Route
          path="mock-exams"
          element={<MockExamPage />}
        />

        {/* Study Plan */}
        <Route
          path="study-plan"
          element={<StudyPlanPage />}
        />

        {/* Upgrade */}
        <Route
          path="upgrade"
          element={<PricingPage />}
        />

        {/* Profile */}
        <Route
          path="profile"
          element={<ProfilePage />}
        />

        <Route path="settings" element={<SettingsPage />} />

        {/* Admin */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminDashboard />
            </ProtectedRoute>
          }
        />

        {/* Unknown app routes */}
        <Route
          path="*"
          element={<Navigate to="/" replace />}
        />
      </Route>

    </Routes>
  );
}


function AppContent() {
  const { theme } = useTheme();
  /*
   * IMPORTANT:
   *
   * There is NO IntroScreen here anymore.
   *
   * The LandingPage is the actual public home page.
   *
   * Authentication decides whether the user sees:
   *
   *     LandingPage
   *          OR
   *     Dashboard
   */
  return (
    <MotionConfig reducedMotion="user">
    <div className="App">

      <BrowserRouter>

        <AuthProvider>

          <SubscriptionProvider>

            <CreditsProvider>

              <AppRouter />

              <Toaster
                position="top-center"
                theme={theme === 'classic' ? 'dark' : 'light'}
                closeButton
              />

            </CreditsProvider>

          </SubscriptionProvider>

        </AuthProvider>

      </BrowserRouter>

    </div>
    </MotionConfig>
  );
}


function App() {
  return <ThemeProvider><AppContent /></ThemeProvider>;
}

export default App;



